import { useEffect, useLayoutEffect } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { trackQuizCompletion, trackQuizInitialization, trackQuizQuestionDisplay } from '@app/pmi/src/comms/events';
import {
  broadcastDomHeightToHost,
  broadcastUserQuizResultToHost,
  isRouteEcommerce,
  saveCrmIdToLocalStorage,
} from '@app/pmi/src/comms/utils';

import { useProducts } from '@lib/core/products/hooks/useProducts';
import { useQuizView } from '@lib/core/quizzes/hooks';
import { isAppInIframe } from '@lib/core/service/utils';
import { POST_MESSAGE_QUIZ_COMPLETED } from '@lib/tools/comms/consts';
import { useComms } from '@lib/tools/comms/hooks';
import { initComms } from '@lib/tools/comms/utils';
import { isProductSentia } from '@lib/tools/shared/pmi/products/filters';
import { recommendProducts } from '@lib/tools/shared/pmi/products/recommender';
import {
  isQuizAnswerTagDissatisfied,
  isQuizAnswerTagRecommendSentia,
  isQuizAnswerTagSatisfied,
} from '@lib/tools/shared/pmi/quizzes/filters';

import { getTechnicalCaptionText } from '@components/pmi/src/utils';

const CommsProvider = ({ children, intl }) => {
  const dispatch = useDispatch();

  const { productsByCharacterCorrelation, productsList } = useProducts();
  const { quizViewAnswerTags, quizViewUserPath, quizViewQuestionId } = useQuizView();
  const { isHostHandshakeApproved } = useComms();

  /**
   * * Setup postMessage listener
   */
  useEffect(() => {
    // Listen for messages from hosting libraries
    if (isAppInIframe) {
      initComms(dispatch);

      if (isRouteEcommerce()) saveCrmIdToLocalStorage();
    }
  }, []);

  /**
   * * Propagate DOM height to host
   */
  useLayoutEffect(() => {
    broadcastDomHeightToHost();
  }, [productsList, productsByCharacterCorrelation]);

  /**
   * * Track quiz initialization
   */
  useEffect(() => {
    const userPathLength = Object.keys(quizViewUserPath).length;
    if (!userPathLength) return;

    if (userPathLength === 1) trackQuizInitialization();
    trackQuizQuestionDisplay(userPathLength, quizViewQuestionId);
  }, [quizViewUserPath]);

  /**
   * * Quiz completion
   */
  useLayoutEffect(() => {
    if (productsByCharacterCorrelation.length) {
      const isSentiaProductAvailable = productsByCharacterCorrelation.some(isProductSentia);
      const isRecommendSentia = quizViewAnswerTags.some(isQuizAnswerTagRecommendSentia);
      const tereaProducts = productsByCharacterCorrelation.filter(gprl => !isProductSentia(gprl));
      const products =
        isSentiaProductAvailable && !isRecommendSentia
          ? recommendProducts(tereaProducts)
          : recommendProducts(productsByCharacterCorrelation);

      if (products) {
        const isQuizDissatisfiedCondition = quizViewAnswerTags.some(isQuizAnswerTagDissatisfied);
        const isQuizSatisfiedCondition = quizViewAnswerTags.some(isQuizAnswerTagSatisfied);

        let technicalCaptionTextNumbers = [];
        if (isQuizSatisfiedCondition) {
          technicalCaptionTextNumbers = [2, 1];
        }
        if (isQuizDissatisfiedCondition) {
          technicalCaptionTextNumbers = [1, 2];
        }
        // ToDo add new technicalCaptionText
        const recommendationCardTextOne = getTechnicalCaptionText(intl, technicalCaptionTextNumbers[0]);
        const recommendationCardTextTwo = getTechnicalCaptionText(intl, technicalCaptionTextNumbers[1]);

        broadcastUserQuizResultToHost(POST_MESSAGE_QUIZ_COMPLETED, {
          products,
          recommendedProductCaptions: [recommendationCardTextOne, recommendationCardTextTwo],
        });

        trackQuizCompletion(products);
      }
    }
  }, [productsByCharacterCorrelation]);

  return (isAppInIframe && isHostHandshakeApproved) || !isAppInIframe ? children : null;
};

export default injectIntl(CommsProvider);
